/*
 * Copyright (c) 2024, King Living
 * Regional phone validation patterns (digits-only, after stripping non-digits).
 * Must stay aligned with King Living FSD - Field Validation (Atlassian).
 * Used by client forms and CRM preference server validation.
 */

/**
 * Map international-prefix digit strings to national form expected by PHONE_REGION_PATTERNS.
 * Digit strings that already start with 0 (national) for AU/NZ/UK/GB/MY are returned unchanged.
 * @param {string} digits - digits only
 * @param {string} countryCode - site / account ISO country
 * @returns {string}
 */
export function normalizePhoneDigitsForRegion(digits, countryCode) {
  const d = (digits || '').trim();
  const code = (countryCode || '').toUpperCase();

  if (!d) return d;

  // National numbers (leading 0) — leave unchanged; still valid with +61 / spaces in the raw input
  if (
    d.startsWith('0') &&
    (code === 'AU' || code === 'NZ' || code === 'UK' || code === 'GB' || code === 'MY')
  ) {
    return d;
  }

  if (code === 'NZ' && d.startsWith('64') && d.length >= 10) {
    const rest = d.slice(2);
    return rest.startsWith('0') ? rest : `0${rest}`;
  }

  if (code === 'AU' && d.startsWith('61') && d.length >= 11) {
    const rest = d.slice(2);
    return rest.startsWith('0') ? rest : `0${rest}`;
  }

  if ((code === 'UK' || code === 'GB') && d.startsWith('44') && d.length >= 12) {
    const rest = d.slice(2);
    return rest.startsWith('0') ? rest : `0${rest}`;
  }

  if ((code === 'US' || code === 'CA') && d.length === 11 && d.startsWith('1')) {
    return d.slice(1);
  }

  if (code === 'SG' && d.startsWith('65') && d.length >= 10) {
    return d.slice(2);
  }

  if (code === 'MY' && d.startsWith('60') && d.length >= 11) {
    const rest = d.slice(2);
    return rest.startsWith('0') ? rest : `0${rest}`;
  }

  return d;
}

/** @type {Record<string, RegExp>} */
export const PHONE_REGION_PATTERNS = {
  AU: /^0[2-478]\d{8}$/,
  NZ: /^0[2-9]\d{7,9}$/,
  UK: /^0\d{9,10}$/,
  GB: /^0\d{9,10}$/,
  US: /^\d{10}$/,
  CA: /^\d{10}$/,
  SG: /^[689]\d{7}$/,
  MY: /^01\d{8,9}$/,
};

/**
 * @param {string} countryCode - ISO country code (e.g. AU, UK, GB)
 * @returns {RegExp}
 */
export function getPhoneRegionPattern(countryCode) {
  const code = (countryCode || '').toUpperCase();

  return PHONE_REGION_PATTERNS[code] || PHONE_REGION_PATTERNS.AU;
}
